import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  PLATFORM_ID,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { combineLatest, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

import { Lang, resolveRoutePath, RouteKey, toLang } from '../../../app.routes-config';
import { ImageFadeDirective } from '../../../directives/image-fade.directive';
import { CalendarEvent } from '../../../models/calendar-event';
import { DirectoryEntry } from '../../../models/directory-entry';
import { Map as GymMap } from '../../../models/map';
import { Nest } from '../../../models/nest';
import { PlayerSearchResult } from '../../../models/player-search-result';
import { PokedexEntry } from '../../../models/pokemon';
import { RaidBoss } from '../../../models/raid-boss';
import { RaidBosses } from '../../../models/raid-bosses';
import { AnalyticsService } from '../../../services/analytics.service';
import { ApiService } from '../../../services/api.service';
import { LanguageService } from '../../../services/language.service';
import { SearchService } from '../../../services/search.service';
import { countryLabel } from '../../../utils/country-names';
import { localizedName } from '../../../utils/localized-name';
import { fuzzyMatchesSearchTerm } from '../../../utils/normalize-search-text';
import { pokedexVariantQueryParams } from '../../../utils/pokedex-link';
import {
  buildPokemonThumbnailUrl,
  buildRaidBossCostumeUrl,
  buildRaidBossSpriteUrl,
  SHADOW_FORM,
  toPokemonThumbnailUrl,
} from '../../../utils/raid-boss-image';
import { teamLabelKey } from '../../../utils/team';
import { Icon } from '../icon/icon';

type SearchResultType = 'community' | 'event' | 'gym' | 'nest' | 'page' | 'player' | 'pokemon' | 'raid-boss';

interface SearchResult {
  globalIndex: number;
  hasColorThumbnail: boolean;
  isAvatar: boolean;
  isShadow: boolean;
  isSprite: boolean;
  resultType: SearchResultType;
  route: string;
  sourceKey: string;
  sourceLabelKey: string;
  subtitle: string;
  thumbnailUrl: string;
  title: string;
}

interface SearchGroup {
  labelKey: string;
  results: SearchResult[];
}

const MIN_QUERY_LENGTH = 2;
const MAX_GYM_RESULTS = 5;
const MAX_RAID_BOSS_RESULTS = 4;
const MAX_EVENT_RESULTS = 5;
const MAX_NEST_RESULTS = 5;
const MAX_COMMUNITY_RESULTS = 5;
const MAX_PLAYER_RESULTS = 5;

const EMPTY_GYM_MAP: GymMap = { count: 0, feature_collection: { features: [], type: 'FeatureCollection' } };
const EMPTY_RAID_BOSSES: RaidBosses = {
  mega: [],
  shadowTierFive: [],
  shadowTierOne: [],
  shadowTierThree: [],
  superMega: [],
  tierFive: [],
  tierOne: [],
  tierThree: [],
};
const EMPTY_NESTS: { list: Nest[]; next_migration: string } = { list: [], next_migration: '' };

const PAGE_ROUTES: { labelKey: string; routeKey: RouteKey }[] = [
  { labelKey: 'nav.home', routeKey: 'home' },
  { labelKey: 'nav.gymMap', routeKey: 'gymMap' },
  { labelKey: 'nav.raidBosses', routeKey: 'raidBosses' },
  { labelKey: 'nav.weather', routeKey: 'weather' },
  { labelKey: 'nav.nests', routeKey: 'nests' },
  { labelKey: 'nav.ranking', routeKey: 'ranking' },
  { labelKey: 'nav.trainerCodes', routeKey: 'trainerCodes' },
  { labelKey: 'nav.pokedex', routeKey: 'pokedex' },
  { labelKey: 'nav.pokedexRanking', routeKey: 'pokedexRanking' },
  { labelKey: 'nav.calendar', routeKey: 'calendar' },
  { labelKey: 'nav.meetups', routeKey: 'meetups' },
  { labelKey: 'nav.directory', routeKey: 'directory' },
  { labelKey: 'nav.usefulLinks', routeKey: 'usefulLinks' },
  { labelKey: 'nav.about', routeKey: 'about' },
  { labelKey: 'nav.profile', routeKey: 'profile' },
  { labelKey: 'megaMenu.suggestGym', routeKey: 'suggestGym' },
  { labelKey: 'megaMenu.suggestNest', routeKey: 'suggestNest' },
];

const buildPokedexResultRoute = (entry: PokedexEntry, lang: Lang): string => {
  const basePath = resolveRoutePath('pokedexDetail', lang, { nationalNumber: entry.national_number });
  const params = pokedexVariantQueryParams(entry.form, entry.type);

  if (!params) return basePath;

  return `${basePath}?form=${encodeURIComponent(params.form)}&type=${encodeURIComponent(params.type)}`;
};

const flattenRaidBosses = (raidBosses: RaidBosses): RaidBoss[] => [
  ...(raidBosses.mega ?? []),
  ...(raidBosses.superMega ?? []),
  ...(raidBosses.tierFive ?? []),
  ...(raidBosses.tierThree ?? []),
  ...(raidBosses.tierOne ?? []),
  ...(raidBosses.shadowTierFive ?? []),
  ...(raidBosses.shadowTierThree ?? []),
  ...(raidBosses.shadowTierOne ?? []),
];

@Component({
  imports: [ReactiveFormsModule, TranslatePipe, Icon, ImageFadeDirective],
  selector: 'app-search-overlay',
  standalone: true,
  styleUrl: './search-overlay.scss',
  templateUrl: './search-overlay.html',
})
export class SearchOverlay {
  readonly searchService = inject(SearchService);

  protected readonly activeIndex = signal<number>(-1);
  protected readonly query = new FormControl('', { nonNullable: true });
  private readonly analytics = inject(AnalyticsService);

  private readonly apiService = inject(ApiService);
  private readonly calendarEvents$ = this.apiService.getCalendarEvents().pipe(
    catchError(() => of<CalendarEvent[]>([])),
    shareReplay({ bufferSize: 1, refCount: false }),
  );
  private readonly communities$ = this.apiService.getDirectoryEntries().pipe(
    catchError(() => of<DirectoryEntry[]>([])),
    shareReplay({ bufferSize: 1, refCount: false }),
  );
  private readonly languageService = inject(LanguageService);

  private readonly raidBosses$ = toObservable(this.languageService.current).pipe(
    switchMap(() => this.apiService.getRaidBosses().pipe(catchError(() => of(EMPTY_RAID_BOSSES)))),
    shareReplay({ bufferSize: 1, refCount: false }),
  );
  protected readonly groups = toSignal(
    this.query.valueChanges.pipe(
      map((value) => value.trim()),
      debounceTime(250),
      distinctUntilChanged(),
      startWith(''),
      tap((term) => {
        if (term.length >= MIN_QUERY_LENGTH) this.analytics.trackEvent('search', { search_term: term });
      }),
      switchMap((term) => {
        if (term.length < MIN_QUERY_LENGTH) return of<SearchGroup[]>([]);

        return combineLatest([
          this.apiService
            .getPokedex({ limit: 6, name: term })
            .pipe(catchError(() => of<{ count: number; list: PokedexEntry[] }>({ count: 0, list: [] }))),
          this.apiService.getGyms({ name: term }).pipe(catchError(() => of(EMPTY_GYM_MAP))),
          this.apiService.getNests({ name: term }).pipe(catchError(() => of(EMPTY_NESTS))),
          this.raidBosses$,
          this.calendarEvents$,
          this.communities$,
          this.apiService.searchPlayers(term, MAX_PLAYER_RESULTS).pipe(catchError(() => of<PlayerSearchResult[]>([]))),
        ]).pipe(
          map(([pokedex, gyms, nests, raidBosses, events, communities, players]) =>
            this.buildGroups(term, pokedex.list, gyms, nests.list, raidBosses, events, communities, players),
          ),
        );
      }),
    ),
    { initialValue: [] as SearchGroup[] },
  );
  protected readonly flatResults = computed(() => this.groups().flatMap((group) => group.results));
  protected readonly activeResult = computed(() => {
    const results = this.flatResults();
    const index = this.activeIndex();

    return index >= 0 && index < results.length ? results[index] : null;
  });

  protected readonly hasResults = computed(() => this.groups().some((group) => group.results.length > 0));
  protected readonly minQueryLength = MIN_QUERY_LENGTH;
  protected readonly trimmedQuery = toSignal(
    this.query.valueChanges.pipe(
      map((value) => value.trim()),
      startWith(''),
    ),
    { initialValue: '' },
  );

  private readonly document = inject(DOCUMENT);

  private readonly platformId = inject(PLATFORM_ID);

  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly resultButtons = viewChildren<ElementRef<HTMLButtonElement>>('resultButton');

  private readonly router = inject(Router);

  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  private readonly translate = inject(TranslateService);

  constructor() {
    effect(() => {
      const open = this.searchService.isOpen();

      if (this.isBrowser) this.document.body.classList.toggle('search-modal-open', open);

      if (!open) {
        this.query.setValue('');
        return;
      }

      if (this.isBrowser) queueMicrotask(() => this.searchInput()?.nativeElement.focus());
    });

    effect(() => {
      this.groups();
      this.activeIndex.set(-1);
    });

    effect(() => {
      const index = this.activeIndex();
      if (!this.isBrowser || index < 0) return;

      this.resultButtons()[index]?.nativeElement.scrollIntoView({ block: 'nearest' });
    });
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.isBrowser) return;

    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.searchService.toggle();
      return;
    }

    if (!this.searchService.isOpen()) return;

    if (event.key === 'Escape') {
      this.searchService.close();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.moveActiveIndex(1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveActiveIndex(-1);
    }
  }

  protected navigateTo(route: string): void {
    this.searchService.close();
    this.router.navigateByUrl(route);
  }

  protected onImageError(event: Event): void {
    (event.target as HTMLImageElement).src = '/icons/default.webp';
  }

  protected onSubmit(): void {
    const result = this.activeResult() ?? this.flatResults()[0];
    if (result) this.openResult(result);
  }

  protected openResult(result: SearchResult): void {
    this.analytics.trackEvent('search_result_select', {
      result_label: result.title,
      result_type: result.resultType,
      search_term: this.trimmedQuery(),
    });
    this.navigateTo(result.route);
  }

  protected setActiveIndex(index: number): void {
    this.activeIndex.set(index);
  }

  private buildGroups(
    term: string,
    pokedexEntries: PokedexEntry[],
    gyms: GymMap,
    nests: Nest[],
    raidBosses: RaidBosses,
    events: CalendarEvent[],
    communities: DirectoryEntry[],
    players: PlayerSearchResult[],
  ): SearchGroup[] {
    const language = this.translate.currentLang();
    const lang = toLang(language);
    const groups: SearchGroup[] = [];

    const pageResults: SearchResult[] = PAGE_ROUTES.filter((page) =>
      fuzzyMatchesSearchTerm(this.translate.instant(page.labelKey), term),
    ).map((page) => ({
      globalIndex: 0,
      hasColorThumbnail: false,
      isAvatar: false,
      isShadow: false,
      isSprite: false,
      resultType: 'page' as const,
      route: resolveRoutePath(page.routeKey, lang),
      sourceKey: page.routeKey,
      sourceLabelKey: 'search.sources.page',
      subtitle: '',
      thumbnailUrl: '/icons/pokemon-choice.webp',
      title: this.translate.instant(page.labelKey),
    }));
    if (pageResults.length > 0) groups.push({ labelKey: 'search.groups.pages', results: pageResults });

    const pokemonResults: SearchResult[] = pokedexEntries.map((entry) => ({
      globalIndex: 0,
      hasColorThumbnail: false,
      isAvatar: false,
      isShadow: entry.form === SHADOW_FORM,
      isSprite: true,
      resultType: 'pokemon' as const,
      route: buildPokedexResultRoute(entry, lang),
      sourceKey: `${entry.national_number}-${entry.type}-${entry.form}`,
      sourceLabelKey: 'search.sources.pokedex',
      subtitle: `#${entry.national_number}`,
      thumbnailUrl: buildPokemonThumbnailUrl(entry),
      title: localizedName(entry.name, entry.english_name, language),
    }));

    const raidBossResults: SearchResult[] = flattenRaidBosses(raidBosses)
      .filter((boss) => fuzzyMatchesSearchTerm(localizedName(boss.name, boss.english_name, language), term))
      .slice(0, MAX_RAID_BOSS_RESULTS)
      .map((boss) => {
        const bossName = localizedName(boss.name, boss.english_name, language);

        return {
          globalIndex: 0,
          hasColorThumbnail: false,
          isAvatar: false,
          isShadow: false,
          isSprite: true,
          resultType: 'raid-boss' as const,
          route: resolveRoutePath('raidBosses', lang),
          sourceKey: `raid-${boss.pokemon.national_number}-${boss.pokemon.form}-${boss.pokemon.type}-${boss.costume}`,
          sourceLabelKey: 'search.sources.raidBoss',
          subtitle: '',
          thumbnailUrl: toPokemonThumbnailUrl(
            boss.costume ? buildRaidBossCostumeUrl(boss) : buildRaidBossSpriteUrl(boss),
          ),
          title: boss.costume ? `${bossName} (${boss.costume_label})` : bossName,
        };
      });

    const pokemonGroupResults = [...pokemonResults, ...raidBossResults];
    if (pokemonGroupResults.length > 0)
      groups.push({ labelKey: 'search.groups.pokemon', results: pokemonGroupResults });

    const gymResults: SearchResult[] = gyms.feature_collection.features.slice(0, MAX_GYM_RESULTS).map((feature) => ({
      globalIndex: 0,
      hasColorThumbnail: false,
      isAvatar: false,
      isShadow: false,
      isSprite: false,
      resultType: 'gym' as const,
      route: resolveRoutePath('gymMap', lang),
      sourceKey: String(feature.properties?.['id'] ?? feature.properties?.['name']),
      sourceLabelKey: 'search.sources.gym',
      subtitle: String(feature.properties?.['city'] ?? ''),
      thumbnailUrl: '/icons/gym.webp',
      title: String(feature.properties?.['name'] ?? ''),
    }));
    if (gymResults.length > 0) groups.push({ labelKey: 'search.groups.gyms', results: gymResults });

    const eventResults: SearchResult[] = events
      .filter((event) => fuzzyMatchesSearchTerm(event.title, term))
      .slice(0, MAX_EVENT_RESULTS)
      .map((event) => ({
        globalIndex: 0,
        hasColorThumbnail: Boolean(event.image_url),
        isAvatar: false,
        isShadow: false,
        isSprite: false,
        resultType: 'event' as const,
        route: `${resolveRoutePath('calendar', lang)}?event=${encodeURIComponent(event.id)}`,
        sourceKey: `event-${event.id}`,
        sourceLabelKey: 'search.sources.event',
        subtitle: '',
        thumbnailUrl: event.image_url || '/icons/task.webp',
        title: event.title,
      }));
    if (eventResults.length > 0) groups.push({ labelKey: 'search.groups.events', results: eventResults });

    const nestResults: SearchResult[] = nests.slice(0, MAX_NEST_RESULTS).map((nest) => ({
      globalIndex: 0,
      hasColorThumbnail: false,
      isAvatar: false,
      isShadow: false,
      isSprite: true,
      resultType: 'nest' as const,
      route: resolveRoutePath('nests', lang),
      sourceKey: `nest-${nest.id}`,
      sourceLabelKey: 'search.sources.nest',
      subtitle: nest.city,
      thumbnailUrl: nest.pokemon ? buildPokemonThumbnailUrl(nest.pokemon) : '/pokemon/thumbs/default.webp',
      title: nest.name,
    }));
    if (nestResults.length > 0) groups.push({ labelKey: 'search.groups.nests', results: nestResults });

    const communityResults: SearchResult[] = communities
      .filter(
        (entry) =>
          fuzzyMatchesSearchTerm(entry.city, term) || fuzzyMatchesSearchTerm(countryLabel(entry.country, lang), term),
      )
      .slice(0, MAX_COMMUNITY_RESULTS)
      .map((entry) => ({
        globalIndex: 0,
        hasColorThumbnail: false,
        isAvatar: false,
        isShadow: false,
        isSprite: false,
        resultType: 'community' as const,
        route: resolveRoutePath('directory', lang),
        sourceKey: `community-${entry.id}`,
        sourceLabelKey: 'search.sources.community',
        subtitle: countryLabel(entry.country, lang),
        thumbnailUrl: '/icons/discord.svg',
        title: entry.city,
      }));
    if (communityResults.length > 0) groups.push({ labelKey: 'search.groups.communities', results: communityResults });

    const playerResults: SearchResult[] = players.map((player) => ({
      globalIndex: 0,
      hasColorThumbnail: Boolean(player.avatar_url),
      isAvatar: Boolean(player.avatar_url),
      isShadow: false,
      isSprite: false,
      resultType: 'player' as const,
      route: resolveRoutePath('playerProfile', lang, { id: player.id }),
      sourceKey: `player-${player.id}`,
      sourceLabelKey: 'search.sources.player',
      subtitle: teamLabelKey(player.team) ? this.translate.instant(teamLabelKey(player.team)) : '',
      thumbnailUrl: player.avatar_url || '/icons/default.webp',
      title: player.name,
    }));
    if (playerResults.length > 0) groups.push({ labelKey: 'search.groups.players', results: playerResults });

    let globalIndex = 0;
    for (const group of groups) {
      for (const result of group.results) {
        result.globalIndex = globalIndex;
        globalIndex += 1;
      }
    }

    return groups;
  }

  private moveActiveIndex(delta: number): void {
    const results = this.flatResults();
    if (results.length === 0) return;

    const currentIndex = this.activeIndex();
    const nextIndex =
      currentIndex < 0
        ? delta > 0
          ? 0
          : results.length - 1
        : (currentIndex + delta + results.length) % results.length;
    this.activeIndex.set(nextIndex);
  }
}
